// Phase 26F.4 · Multi-dimension bulk target import UI.
//
// Uses the inline fetch → blob → anchor pattern (per 26F.3 Item A SOP —
// bypass shared helpers to avoid any bundler/SW-cache anomaly). Shows the
// Applied / Skipped / Errors result panel with an "Export errors" link.
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, ClipboardList, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function TargetBulkImport() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const doDownloadTemplate = async () => {
    // eslint-disable-next-line no-console
    console.log("[26f4-target-template] click fired");
    try {
      const token = localStorage.getItem("tulsi_token") || localStorage.getItem("token") || "";
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${base}/api/v1/targets/bulk-import/template.xlsx`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "bulk_targets_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[26f4-target-template] failed", e);
      toast.error(`Download failed: ${e?.message || e}`);
    }
  };

  const doImport = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const token = localStorage.getItem("tulsi_token") || localStorage.getItem("token") || "";
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${base}/api/v1/targets/bulk-import/multi`,
        { method: "POST", body: fd,
          headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json();
      // v2 envelope: {error:{code,message,details?}}; legacy: {detail: ...}.
      if (!res.ok) throw new Error(body?.error?.message || body?.detail || `HTTP ${res.status}`);
      setResult(body);
      const nA = Array.isArray(body?.applied) ? body.applied.length : 0;
      const nS = Array.isArray(body?.skipped) ? body.skipped.length : 0;
      const nE = Array.isArray(body?.errors) ? body.errors.length : 0;
      if (nA > 0) toast.success(`Applied ${nA} · Skipped ${nS} · Errors ${nE}`);
      else if (nE > 0) toast.warning(`No applies · ${nE} error row(s), ${nS} skipped`);
      else toast.info(`No changes · ${nS} row(s) already at target`);
    } catch (e) {
      toast.error(`Import failed: ${e?.message || e}`);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const exportErrors = () => {
    if (!result?.errors?.length) return;
    const rows = [
      ["row_index", "error_code", "message", "offending_cell"],
      ...result.errors.map(e => [e.row_index, e.error_code, e.message, e.offending_cell || ""]),
    ];
    const csv = rows.map(r =>
      r.map(c => `"${String(c).replaceAll(`"`,`""`)}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `target_import_errors_${result.import_batch_id?.slice(0,8) || "batch"}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="target-bulk-import-page">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-[#6D2C91]" /> Bulk Target Upload
        </h1>
        <p className="text-xs text-neutral-500 mt-0.5">
          Multi-dimension targets: Value (₹), SKU volume (PCS/KG), Category volume,
          Distributor / Zone scope, Visit count, Coverage count. Month or Quarter frequency.
        </p>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={doDownloadTemplate}
                    data-testid="target-import-template-btn"
                    type="button">
              <Download className="h-4 w-4 mr-1" /> Download template
            </Button>
            <input type="file" ref={fileRef}
                    accept=".xlsx,.xlsm,.csv"
                    onChange={(e) => doImport(e.target.files?.[0])}
                    className="hidden"
                    data-testid="target-import-file-input" />
            <Button onClick={() => fileRef.current?.click()}
                    disabled={busy}
                    className="bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                    data-testid="target-import-btn"
                    type="button">
              <Upload className="h-4 w-4 mr-1" />
              {busy ? "Importing…" : "Import via Excel"}
            </Button>
            <span className="text-[11px] text-neutral-500 ml-2">
              Accepted formats: xlsx, xlsm, csv
            </span>
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card className="border-neutral-200 rounded-2xl" data-testid="target-import-result">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <CheckCircle2 className="h-4 w-4" /> Applied
                <span data-testid="result-applied-count">
                  {result.applied?.length || 0}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-emerald-700 max-h-40 overflow-y-auto space-y-0.5">
                {(result.applied || []).slice(0, 20).map((a, i) => (
                  <div key={i}>row {a.row_index} · {a.dimension} · {a.op || "applied"}</div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-center gap-2 text-amber-800 font-semibold">
                <AlertTriangle className="h-4 w-4" /> Skipped
                <span data-testid="result-skipped-count">
                  {result.skipped?.length || 0}
                </span>
              </div>
              <div className="mt-1 text-[11px] text-amber-700 max-h-40 overflow-y-auto space-y-0.5">
                {(result.skipped || []).slice(0, 20).map((s, i) => (
                  <div key={i}>row {s.row_index} · {s.dimension} · {s.status}</div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <div className="flex items-center gap-2 text-red-800 font-semibold">
                <XCircle className="h-4 w-4" /> Errors
                <span data-testid="result-errors-count">
                  {result.errors?.length || 0}
                </span>
                {result.errors?.length > 0 && (
                  <button onClick={exportErrors}
                          className="ml-auto text-[11px] underline hover:text-red-900"
                          data-testid="target-import-export-errors"
                          type="button">
                    Export errors CSV
                  </button>
                )}
              </div>
              <div className="mt-1 text-[11px] text-red-700 max-h-40 overflow-y-auto space-y-0.5">
                {(result.errors || []).slice(0, 20).map((e, i) => (
                  <div key={i}>row {e.row_index} · {e.error_code}
                    {e.offending_cell ? ` · cell=${e.offending_cell}` : ""}</div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
